import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import { formatDate } from 'utils/format';
import { PriceRequest } from 'models/price-request';
import PriceRequestStatusChip from './price-request-status-chip.component';

const Field: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
    <Box minWidth={160}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" component="div">
            {children}
        </Typography>
    </Box>
);

/** DD-3697: number, parties, status, action set and currency of one request. */
const PriceRequestHeader: FC<{ request: PriceRequest }> = ({ request }) => {
    const { t: translate } = useTranslation('price-requests');
    return (
        <Paper sx={{ p: 2, mb: 2 }} data-test="price-request.header">
            <Box display="flex" gap={3} flexWrap="wrap">
                <Field label={translate('STATUS')}>
                    <PriceRequestStatusChip status={request.status} />
                </Field>
                <Field label={translate('CHAIN')}>{request.chain.name || '–'}</Field>
                <Field label={translate('LAB')}>{request.lab.name || '–'}</Field>
                <Field label={translate('ACTION_SET')}>{request.actionSetTitle || '–'}</Field>
                <Field label={translate('CURRENCY')}>{request.currency}</Field>
                <Field label={translate('OPENED_BY')}>
                    {translate(`OPENED_BY_${request.initiatorSide}`)}
                    {request.createdByName ? ` · ${request.createdByName}` : ''}
                </Field>
                <Field label={translate('CREATED')}>{formatDate(request.createdAt, 'date')}</Field>
            </Box>
            {request.note ? (
                <Box mt={2}>
                    <Field label={translate('NOTE')}>
                        <Box sx={{ whiteSpace: 'pre-wrap' }}>{request.note}</Box>
                    </Field>
                </Box>
            ) : undefined}
        </Paper>
    );
};

export default PriceRequestHeader;
